import { Suspense, lazy, useCallback, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import type { KartenPunkt } from '../components/Karte.js';
import { SkalaPunkte } from '../components/SkalaAnzeige.js';
import { gebieteImJahr, normalisiere, skalaKurztext, type GebietImJahr } from '../daten/ansicht.js';
import type { Daten } from '../daten/laden.js';
import { SKALA_MODUS } from '../daten/skala.js';
import { JAHR_MAX, JAHR_MIN, KERN_BIS, KERN_VON, type Kategorie } from '../daten/zeitreihe.js';
import { KATEGORIE_REIHENFOLGE, KATEGORIE_STIL, SKALA_FARBEN, SKALA_OFFEN } from '../farben.js';

// MapLibre ist groß – erst laden, wenn die Karte gebraucht wird (Gebietsseiten kommen ohne aus).
const Karte = lazy(() => import('../components/Karte.js').then((m) => ({ default: m.Karte })));

type Faerbung = 'konzessionaer' | 'skala';
const START_JAHR = 2015;
const LISTE_KURZ = 50;

function punktFuer(g: GebietImJahr, faerbung: Faerbung, jahr: number): KartenPunkt {
  const p = g.feature.properties;
  const [lon, lat] = g.feature.geometry.coordinates;
  const hinweis = [p.name, `${jahr}: ${g.konzessionaerText}`];
  const skalaText = skalaKurztext(g.skala);
  if (skalaText) hinweis.push(skalaText);
  if (faerbung === 'skala' && g.skala) {
    const s = g.skala;
    const bewertet = s.wert !== null && s.status !== 'offen';
    return {
      slug: p.slug,
      name: p.name,
      lon,
      lat,
      fuellung: bewertet ? SKALA_FARBEN[s.wert!]! : SKALA_OFFEN.fuellung,
      rand: bewertet ? (s.status === 'vorläufig' ? '#1c1917' : '#ffffff') : SKALA_OFFEN.rand,
      radius: s.referenz ? 9 : bewertet ? 7 : 3.5,
      deckkraft: bewertet ? 1 : 0.8,
      ebene: bewertet ? 10 : 1,
      hinweis,
    };
  }
  const stil = KATEGORIE_STIL[g.kategorie];
  return {
    slug: p.slug,
    name: p.name,
    lon,
    lat,
    fuellung: stil.fuellung,
    rand: stil.rand,
    radius: stil.radius,
    deckkraft: stil.deckkraft,
    ebene: g.kategorie === 'keine_fassung' ? 0 : g.kategorie === 'ohne_angabe' ? 1 : 5,
    hinweis,
  };
}

function leseKategorien(wert: string | null): Set<Kategorie> {
  if (!wert) return new Set(KATEGORIE_REIHENFOLGE);
  return new Set(wert.split(',').filter((k): k is Kategorie => (KATEGORIE_REIHENFOLGE as string[]).includes(k)));
}

export function Uebersicht({ daten }: { daten: Daten }) {
  const [params, setParams] = useSearchParams();
  const navigate = useNavigate();
  const [alleZeigen, setAlleZeigen] = useState(false);

  const jahrRoh = Number(params.get('jahr'));
  const jahr = Number.isInteger(jahrRoh) && jahrRoh >= JAHR_MIN && jahrRoh <= JAHR_MAX ? jahrRoh : START_JAHR;
  const faerbung: Faerbung = params.get('farbe') === 'skala' && SKALA_MODUS !== 'aus' ? 'skala' : 'konzessionaer';
  const land = params.get('land') ?? '';
  const suche = params.get('q') ?? '';
  const kategorien = leseKategorien(params.get('kat'));

  const setze = useCallback(
    (aenderung: Record<string, string | null>) => {
      const neu = new URLSearchParams(params);
      for (const [k, v] of Object.entries(aenderung)) {
        if (v === null || v === '') neu.delete(k);
        else neu.set(k, v);
      }
      setParams(neu, { replace: true });
    },
    [params, setParams],
  );

  const laender = useMemo(
    () =>
      [...new Set(daten.gebiete.features.map((f) => f.properties.bundesland).filter((b): b is string => !!b))].sort(
        (a, b) => a.localeCompare(b, 'de'),
      ),
    [daten],
  );

  const imJahr = useMemo(() => gebieteImJahr(daten, jahr), [daten, jahr]);

  // Vorfilter (Land, Suche) – die Legende zählt danach, damit ihre Zahlen beim Ab-/Anwählen stabil bleiben
  const vorgefiltert = useMemo(() => {
    const q = normalisiere(suche.trim());
    return imJahr.filter((g) => {
      const p = g.feature.properties;
      if (land && p.bundesland !== land) return false;
      if (q && !normalisiere(p.name).includes(q)) return false;
      return true;
    });
  }, [imJahr, land, suche]);

  const zaehler = useMemo(() => {
    const z = Object.fromEntries(KATEGORIE_REIHENFOLGE.map((k) => [k, 0])) as Record<Kategorie, number>;
    for (const g of vorgefiltert) z[g.kategorie]++;
    return z;
  }, [vorgefiltert]);

  const sichtbar = useMemo(
    () =>
      vorgefiltert
        .filter((g) => kategorien.has(g.kategorie))
        .sort((a, b) => a.feature.properties.name.localeCompare(b.feature.properties.name, 'de')),
    [vorgefiltert, kategorien],
  );

  const punkte = useMemo(() => sichtbar.map((g) => punktFuer(g, faerbung, jahr)), [sichtbar, faerbung, jahr]);
  const zurGebietsseite = useCallback((slug: string) => navigate(`/gebiet/${slug}`), [navigate]);

  const kategorieUmschalten = (k: Kategorie) => {
    const neu = new Set(kategorien);
    if (neu.has(k)) neu.delete(k);
    else neu.add(k);
    setze({ kat: neu.size === KATEGORIE_REIHENFOLGE.length ? null : [...neu].join(',') || 'keine' });
  };

  const liste = alleZeigen ? sichtbar : sichtbar.slice(0, LISTE_KURZ);
  const skalaZaehler = useMemo(() => {
    const z = { bewertet: 0, vorlaeufig: 0 };
    for (const g of imJahr) {
      if (g.skala?.status === 'bewertet') z.bewertet++;
      if (g.skala?.status === 'vorläufig') z.vorlaeufig++;
    }
    return z;
  }, [imJahr]);

  return (
    <div className="uebersicht">
      <section className="panel" aria-labelledby="panel-titel">
        <h1 id="panel-titel" className="panel-titel">
          Konzessionsgebiete
        </h1>
        <p className="panel-text">
          Für jedes Gebiet zeigt die Karte, welcher Konzessionär nach der jeweils geltenden Technischen
          Anschlussbedingung (TAB) die Aufschaltung von Brandmeldeanlagen auf die Leitstelle übernahm.
        </p>

        <div className="block">
          <label htmlFor="jahr" className="block-titel">
            Stand zum 31.12. <output htmlFor="jahr" className="jahr-anzeige">{jahr}</output>
          </label>
          <div className="jahr-steuerung">
            <button
              type="button"
              className="knopf-klein"
              onClick={() => setze({ jahr: String(Math.max(JAHR_MIN, jahr - 1)) })}
              disabled={jahr <= JAHR_MIN}
              aria-label="Ein Jahr zurück"
            >
              ‹
            </button>
            <input
              id="jahr"
              type="range"
              min={JAHR_MIN}
              max={JAHR_MAX}
              step={1}
              value={jahr}
              list="jahr-marken"
              onChange={(e) => setze({ jahr: e.target.value })}
              aria-valuetext={`Jahr ${jahr}`}
            />
            <button
              type="button"
              className="knopf-klein"
              onClick={() => setze({ jahr: String(Math.min(JAHR_MAX, jahr + 1)) })}
              disabled={jahr >= JAHR_MAX}
              aria-label="Ein Jahr vor"
            >
              ›
            </button>
          </div>
          <datalist id="jahr-marken">
            {[JAHR_MIN, KERN_VON, KERN_BIS, JAHR_MAX].map((j) => (
              <option key={j} value={j} label={String(j)} />
            ))}
          </datalist>
          <div className="jahr-skala" aria-hidden="true">
            <span>{JAHR_MIN}</span>
            <span className="jahr-kern">
              Kernzeitraum {KERN_VON}–{KERN_BIS}
            </span>
            <span>{JAHR_MAX}</span>
          </div>
        </div>

        {SKALA_MODUS !== 'aus' && (
          <fieldset className="block">
            <legend className="block-titel">Färbung</legend>
            <div className="umschalter">
              <label>
                <input
                  type="radio"
                  name="farbe"
                  checked={faerbung === 'konzessionaer'}
                  onChange={() => setze({ farbe: null })}
                />{' '}
                Konzessionär
              </label>
              <label>
                <input
                  type="radio"
                  name="farbe"
                  checked={faerbung === 'skala'}
                  onChange={() => setze({ farbe: 'skala' })}
                />{' '}
                Vergleichbarkeit mit Berlin
              </label>
            </div>
          </fieldset>
        )}

        {faerbung === 'konzessionaer' ? (
          <fieldset className="block">
            <legend className="block-titel">Konzessionär {jahr}</legend>
            <ul className="legende">
              {KATEGORIE_REIHENFOLGE.map((k) => {
                const stil = KATEGORIE_STIL[k];
                return (
                  <li key={k}>
                    <label className="legende-eintrag">
                      <input type="checkbox" checked={kategorien.has(k)} onChange={() => kategorieUmschalten(k)} />
                      <span
                        className="legende-punkt"
                        style={{
                          background: stil.fuellung,
                          borderColor: stil.rand === '#ffffff' ? stil.fuellung : stil.rand,
                          opacity: stil.deckkraft,
                          width: stil.radius * 2,
                          height: stil.radius * 2,
                        }}
                        aria-hidden="true"
                      />
                      <span className="legende-text">{stil.label}</span>
                      <span className="legende-zahl">{zaehler[k]}</span>
                    </label>
                  </li>
                );
              })}
            </ul>
          </fieldset>
        ) : (
          <div className="block">
            <div className="block-titel">Vergleichbarkeit mit Berlin (0–5)</div>
            <ul className="legende">
              {SKALA_FARBEN.map((farbe, wert) => (
                <li key={farbe} className="legende-eintrag">
                  <span className="legende-punkt" style={{ background: farbe, borderColor: farbe }} aria-hidden="true" />
                  <span className="legende-text">{wert}</span>
                </li>
              ))}
              <li className="legende-eintrag">
                <span
                  className="legende-punkt legende-punkt-klein"
                  style={{ background: SKALA_OFFEN.fuellung, borderColor: SKALA_OFFEN.rand }}
                  aria-hidden="true"
                />
                <span className="legende-text">noch nicht bewertet</span>
              </li>
              <li className="legende-eintrag">
                <span className="legende-punkt" style={{ background: SKALA_FARBEN[3], borderColor: '#1c1917' }} aria-hidden="true" />
                <span className="legende-text">dunkler Rand = vorläufig</span>
              </li>
            </ul>
            <p className="hinweis-klein">
              Bewertet: {skalaZaehler.bewertet}, vorläufig: {skalaZaehler.vorlaeufig}. Die übrigen Gebiete werden derzeit
              ausgewertet.
            </p>
          </div>
        )}

        <div className="block filter">
          <label className="feld">
            <span className="feld-label">Bundesland</span>
            <select value={land} onChange={(e) => setze({ land: e.target.value || null })}>
              <option value="">alle</option>
              {laender.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
          </label>
          <label className="feld">
            <span className="feld-label">Gebiet suchen</span>
            <input
              type="search"
              value={suche}
              placeholder="z. B. Hamburg"
              onChange={(e) => setze({ q: e.target.value || null })}
            />
          </label>
        </div>

        <div className="block">
          <h2 className="block-titel" aria-live="polite">
            {sichtbar.length === 1 ? '1 Gebiet' : `${sichtbar.length} Gebiete`}
          </h2>
          {sichtbar.length === 0 ? (
            <p className="leer">
              Keine Gebiete für diese Auswahl.{' '}
              <button type="button" className="text-knopf" onClick={() => setParams(new URLSearchParams({ jahr: String(jahr) }))}>
                Filter zurücksetzen
              </button>
            </p>
          ) : (
            <ul className="gebietsliste">
              {liste.map((g) => {
                const p = g.feature.properties;
                const stil = KATEGORIE_STIL[g.kategorie];
                return (
                  <li key={p.slug}>
                    <Link to={`/gebiet/${p.slug}`} className="gebietsliste-eintrag">
                      <span
                        className="legende-punkt"
                        style={{ background: stil.fuellung, borderColor: stil.rand === '#ffffff' ? stil.fuellung : stil.rand }}
                        aria-hidden="true"
                      />
                      <span className="gebietsliste-text">
                        <span className="gebietsliste-name">{p.name}</span>
                        <span className="gebietsliste-detail">
                          {p.bundesland ?? '–'} · {g.konzessionaerText}
                        </span>
                      </span>
                      {g.skala && g.skala.wert !== null && <SkalaPunkte eintrag={g.skala} klein />}
                    </Link>
                  </li>
                );
              })}
            </ul>
          )}
          {!alleZeigen && sichtbar.length > LISTE_KURZ && (
            <button type="button" className="knopf-sekundaer" onClick={() => setAlleZeigen(true)}>
              Alle {sichtbar.length} Gebiete anzeigen
            </button>
          )}
        </div>
      </section>
      <section className="karte-bereich" aria-label="Karte">
        <Suspense
          fallback={
            <div className="zustand" role="status">
              <span className="spinner" aria-hidden="true" /> Karte wird geladen …
            </div>
          }
        >
          <Karte punkte={punkte} onAuswahl={zurGebietsseite} />
        </Suspense>
      </section>
    </div>
  );
}
